import { createContext, useContext, useState, ReactNode } from "react";
import { MessageContext } from "@/types/messageContext";

interface ProfileMessagingContextType {
  isOpen: boolean;
  activeRecipientId: string | null;
  initialContext?: MessageContext;
  openDrawer: (userId: string, context?: MessageContext) => void;
  closeDrawer: () => void;
}

const ProfileMessagingContext = createContext<ProfileMessagingContextType | undefined>(undefined);

export function ProfileMessagingProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeRecipientId, setActiveRecipientId] = useState<string | null>(null);
  const [initialContext, setInitialContext] = useState<MessageContext | undefined>();

  const openDrawer = (userId: string, context?: MessageContext) => {
    setActiveRecipientId(userId);
    setInitialContext(context);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    // Delay clearing recipient to allow animations
    setTimeout(() => {
      setActiveRecipientId(null);
      setInitialContext(undefined);
    }, 300);
  };

  return (
    <ProfileMessagingContext.Provider
      value={{
        isOpen,
        activeRecipientId,
        initialContext,
        openDrawer,
        closeDrawer,
      }}
    >
      {children}
    </ProfileMessagingContext.Provider>
  );
}

export function useProfileMessaging() {
  const context = useContext(ProfileMessagingContext);
  if (!context) {
    throw new Error("useProfileMessaging must be used within ProfileMessagingProvider");
  }
  return context;
}
